"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { routes, sections, site } from "@/lib/site";
import ThemeToggle from "./ThemeToggle";

/**
 * Sticky site header. Desktop: horizontal nav. Mobile: disclosure menu.
 *
 * Stacking contract (fixes the untappable mobile menu):
 *  - header      z-50  (sticky, stacking context root)
 *  - backdrop    z-40  (fixed, dims the page behind the open menu)
 *  - mobile nav  z-50   (explicit, paints and RECEIVES TAPS above the backdrop)
 * Without the explicit z on the nav, the earlier-in-DOM fixed backdrop
 * covered it and swallowed all pointer events.
 *
 * Nav items are two visually distinct classes:
 *  - Anchors (in-page sections) — quiet text links
 *  - Pages (standalone routes)  — bordered pills
 */
export default function Header() {
  const pathname = usePathname();
  const isHome = pathname === "/";
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("");
  const [scrolled, setScrolled] = useState(false);

  // Shadow the bar after scrolling past the hero. The initial read of
  // scroll position happens inside the scroll subscriber, not in the
  // effect body — no synchronous cascading render.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu when the route changes.
  useEffect(() => {
    if (!open) return;
    const onPop = () => setOpen(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open, pathname]);

  // Track the section in view (homepage only).
  useEffect(() => {
    if (!isHome) return;
    const ids = ["home", ...sections.map((s) => s.id)];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [isHome]);

  // Lock scroll and wire Escape while the mobile menu is open.
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const anchorItems = (onNavigate?: () => void) => (
    <>
      {isHome
        ? sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={onNavigate}
              aria-current={active === s.id ? "true" : undefined}
              className={`rounded px-2.5 py-1.5 text-sm transition-colors ${
                active === s.id
                  ? "text-accent"
                  : "text-muted hover:text-ink"
              }`}
            >
              {s.label}
            </a>
          ))
        : null}
    </>
  );

  const pageItems = (onNavigate?: () => void) => (
    <>
      {routes.map((r) => (
        <Link
          key={r.href}
          href={r.href}
          onClick={onNavigate}
          aria-current={pathname.startsWith(r.href) ? "page" : undefined}
          className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
            pathname.startsWith(r.href)
              ? "border-accent/60 bg-accent-soft text-accent"
              : "border-line text-muted hover:border-line-strong hover:text-ink"
          }`}
        >
          {r.label}
        </Link>
      ))}
    </>
  );

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-shadow ${
        scrolled
          ? "border-line bg-bg shadow-[0_1px_12px_rgba(0,0,0,0.25)]"
          : "border-line bg-bg"
      }`}
    >
      <div className="shell flex h-16 items-center justify-between gap-4">
        <Link
          href="/"
          className="flex items-center gap-2.5 rounded"
          aria-label={`${site.name} — home`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded border border-line-strong bg-surface font-mono text-xs font-medium tracking-tight text-accent">
            {site.initials}
          </span>
          <span className="hidden flex-col leading-tight min-[420px]:flex">
            <span className="text-sm font-semibold">{site.name}</span>
            <span className="font-mono text-[11px] text-faint">
              {site.role}
            </span>
          </span>
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          {anchorItems()}
          <span
            aria-hidden="true"
            className={`mx-2 h-5 w-px bg-line ${isHome ? "" : "hidden"}`}
          />
          {pageItems()}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            className="relative z-50 inline-flex h-9 w-9 items-center justify-center rounded-md border border-line text-muted transition-colors hover:border-line-strong hover:text-ink md:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              {open ? (
                <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
              ) : (
                <path d="M2 4.5h12M2 8h12M2 11.5h12" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu — backdrop dims the page, nav sits explicitly above it */}
      {open && (
        <>
          <div
            className="fixed inset-0 top-16 z-40 bg-bg/60 backdrop-blur-sm md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <nav
            id="mobile-nav"
            aria-label="Primary"
            className="relative z-50 border-t border-line bg-bg px-1 pb-4 pt-2 md:hidden"
          >
            <div className="shell flex flex-col">
              {isHome ? (
                <>
                  <p className="px-2.5 pb-1 pt-2 font-mono text-[11px] uppercase tracking-widest text-faint">
                    On this page
                  </p>
                  {anchorItems(() => setOpen(false))}
                  <p className="px-2.5 pb-1 pt-3 font-mono text-[11px] uppercase tracking-widest text-faint">
                    Pages
                  </p>
                </>
              ) : null}
              {pageItems(() => setOpen(false))}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}